import { type FirebaseApp, initializeApp } from "firebase/app";
import { type Auth, connectAuthEmulator, getAuth } from "firebase/auth";
import {
  connectFirestoreEmulator,
  type Firestore,
  getFirestore,
} from "firebase/firestore";
import { firebaseConfig } from "./firebaseConfig";

/**
 * Firebase wiring.
 *
 * In dev this points at the local emulator suite — no cloud project, no cost,
 * no rate limits, and deterministic state you can wipe between runs. That is
 * what makes the app testable without credentials, so it is the default rather
 * than an opt-in.
 *
 * `npm run emulators` starts them. See docs/engineering/conventions.md.
 */

const USE_EMULATORS =
  import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS !== "false";

// The emulators ignore these values entirely, so dev works with the real ones
// in place — one config, not two, and no chance of them drifting apart.
export const app: FirebaseApp = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app);

export const usingEmulators = USE_EMULATORS;

if (USE_EMULATORS) {
  // Vite HMR can re-run this module; the SDK throws if we connect twice.
  const g = globalThis as { __larderEmulators?: boolean };
  if (!g.__larderEmulators) {
    g.__larderEmulators = true;
    const host = window.location.hostname; // works from a phone on the LAN too
    connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
    connectFirestoreEmulator(db, host, 8080);
  }
}
